import { LoadingState } from "@/components/ui/loading-state";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Download, Loader2, Upload } from "lucide-react";

import {
  downloadDatabookVersion,
  listDatabookVersions,
  uploadDatabookVersion,
  type DatabookVersion,
} from "@/lib/api/databook";
import { uploadVersionAndRefresh } from "./databookVersionManagementActions";

interface DatabookVersionListProps {
  versions: DatabookVersion[];
  canEdit: boolean;
  uploading: boolean;
  downloadingVersionId: string | null;
  onDownload: (versionId: string) => void;
  onChooseUpload: () => void;
  selectedVersionId?: string;
  onView: (versionId: string) => void;
}

export function DatabookVersionList({
  versions,
  canEdit,
  uploading,
  downloadingVersionId,
  onDownload,
  onChooseUpload,
  selectedVersionId,
  onView,
}: DatabookVersionListProps) {
  return (
    <>
      {versions.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          No persisted databook versions yet.
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto p-1">
          {versions.map((version, index) => (
            <div
              key={version.id}
              className={`flex items-center rounded-md pr-1 ${selectedVersionId === version.id ? "bg-muted" : ""}`}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-3 py-2.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                aria-label={`Select version ${version.version_number}`}
                aria-pressed={selectedVersionId === version.id}
                onClick={() => onView(version.id)}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-[12.5px] font-medium">
                    Version {version.version_number}
                    {index === 0 && (
                      <span className="rounded bg-foreground/5 px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                        Latest
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {version.created_at
                      ? new Date(version.created_at).toLocaleString()
                      : "Creation time unavailable"}
                  </span>
                </span>
                {selectedVersionId === version.id && <Check size={14} className="shrink-0" aria-hidden="true" />}
              </button>
              <button
                type="button"
                aria-label={`Download version ${version.version_number}`}
                title={`Download version ${version.version_number}`}
                disabled={downloadingVersionId !== null}
                onClick={() => onDownload(version.id)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-wait disabled:opacity-50"
              >
                {downloadingVersionId === version.id
                  ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                  : <Download size={14} aria-hidden="true" />}
              </button>
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="border-t border-border p-1">
          <button
            type="button"
            onClick={onChooseUpload}
            disabled={uploading}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-[12px] font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading…" : "Upload edited .xlsx"}
          </button>
        </div>
      )}
    </>
  );
}

interface DatabookVersionManagementProps {
  projectId?: string;
  canEdit: boolean;
  selectedVersionId?: string;
  onView: (versionId: string) => void;
  onUploaded?: () => Promise<void>;
}

export function DatabookVersionManagement({
  projectId,
  canEdit,
  selectedVersionId,
  onView,
  onUploaded,
}: DatabookVersionManagementProps) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingVersionId, setDownloadingVersionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const versionsQuery = useQuery({
    queryKey: ["databook-versions", projectId],
    queryFn: () => listDatabookVersions(projectId!),
    enabled: open && !!projectId,
  });

  if (!projectId) return null;

  const handleDownload = async (versionId: string) => {
    if (downloadingVersionId !== null) return;
    setActionError(null);
    setDownloadingVersionId(versionId);
    try {
      await downloadDatabookVersion(projectId, versionId);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Download failed.");
      setOpen(true);
    } finally {
      setDownloadingVersionId(null);
    }
  };

  const handleUpload = async (file: File) => {
    setActionError(null);
    setUploading(true);
    try {
      await uploadVersionAndRefresh(
        file,
        (selectedFile) => uploadDatabookVersion(projectId, selectedFile),
        async () => {
          await versionsQuery.refetch();
          await onUploaded?.();
        },
      );
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Upload failed.");
      setOpen(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        aria-label="Upload edited databook"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleUpload(file);
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Choose databook version"
            title="Choose databook version"
            className={`flex min-h-7 shrink-0 items-center justify-center self-stretch px-2 text-background hover:bg-background/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${selectedVersionId ? "rounded-r-lg border-l border-background/20" : "rounded-lg"}`}
          >
            {uploading
              ? <Loader2 size={12} className="animate-spin" />
              : <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg p-0" aria-label="Databook versions">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-[12.5px] font-semibold">Databook versions</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Select to view, or download any version.</p>
          </div>
          {versionsQuery.isLoading ? (
            <div className="p-4"><LoadingState /></div>
          ) : versionsQuery.isError ? (
            <div role="alert" className="p-4 text-xs text-destructive">
              Version history could not be loaded.
              <button type="button" className="ml-1 underline" onClick={() => void versionsQuery.refetch()}>Retry</button>
            </div>
          ) : (
            <DatabookVersionList
              versions={versionsQuery.data ?? []}
              selectedVersionId={selectedVersionId}
              onView={(versionId) => {
                if (versionId !== selectedVersionId) onView(versionId);
                setOpen(false);
              }}
              canEdit={canEdit}
              uploading={uploading}
              downloadingVersionId={downloadingVersionId}
              onDownload={(versionId) => void handleDownload(versionId)}
              onChooseUpload={() => fileInputRef.current?.click()}
            />
          )}
          {actionError && <p role="alert" className="px-4 pb-3 text-xs text-destructive">{actionError}</p>}
        </PopoverContent>
      </Popover>
    </>
  );
}
